import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { PermissionGuard } from 'app/core/permissions/permissions.guard';
import { TabbedPageLayoutComponent } from 'app/layout/page-layouts/tabbed/tabbed-page-layout.component';
import { InnerTabbedPageLayoutComponent } from 'app/layout/page-layouts/inner/tabbed/inner-tabbed-page-layout.component';
import { playerResolver } from './resolvers/player/player.resolver';

export const playerRoutes: Route[] = [
    {
        path: 'players',
        canActivate: [AuthGuard],
        component: LayoutComponent,
        children: [
            // ---------------------------------------------------------------------------------
            // Players List
            // ---------------------------------------------------------------------------------
            {
                path: '',
                canActivate: [PermissionGuard],
                data: {
                    breadcrumbs: [{ label: 'navigation.players.title' }],
                    actions: [
                        {
                            label: '',
                            icon: 'heroicons_outline:cog-6-tooth',
                            execute: (componentInstance) => {
                                componentInstance.openTableColumnSettings();
                            },
                        },
                        {
                            label: 'actions.export',
                            icon: 'heroicons_outline:document-arrow-down',
                            execute: (componentInstance) => {
                                componentInstance.exportTableData();
                            },
                        },
                    ],
                },
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        loadComponent: () =>
                            import('./pages/list/player-list.component').then(
                                (c) => c.PlayerListComponent
                            ),
                    },
                ],
            },
            // ---------------------------------------------------------------------------------
            // Locked
            // ---------------------------------------------------------------------------------
            {
                path: 'locked',
                canActivate: [PermissionGuard],
                data: {
                    breadcrumbs: [{ label: 'players.locked' }],
                },
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        loadComponent: () =>
                            import('./pages/locked/locked-players.component').then(
                                (c) => c.LockedPlayersComponent
                            ),
                    },
                ],
            },
            // ---------------------------------------------------------------------------------
            // Messages List
            // ---------------------------------------------------------------------------------
            {
                path: 'messages-list',
                canActivate: [PermissionGuard],
                data: {
                    breadcrumbs: [{ label: 'players.messages-list.title' }],
                    actions: [
                        {
                            label: '',
                            icon: 'heroicons_outline:cog-6-tooth',
                            execute: (componentInstance) => {
                                componentInstance.openTableColumnSettings();
                            },
                        },
                        {
                            label: 'actions.new',
                            route: '/players/messages-list/new',
                        },
                    ],
                },
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        loadComponent: () =>
                            import('./pages/msg-list/msg-list.component').then(
                                (c) => c.MessagesListComponent
                            ),
                    },
                    {
                        path: 'new',
                        canActivate: [PermissionGuard],
                        data: {
                            showBulkActionButton: false,
                            breadcrumbs: [
                                {
                                    label: 'players.messages-list.title',
                                    link: '/players/messages-list',
                                },
                                { label: 'players.messages-list.new' },
                            ],
                            actions: [
                                {
                                    label: '',
                                    icon: 'heroicons_outline:cog-6-tooth',
                                    execute: (componentInstance) => {
                                        componentInstance.openTableColumnSettings();
                                    },
                                },
                            ],
                            alwaysVisibleActions: [
                                {
                                    label: 'players.messages-list.sendToAll',
                                    icon: 'heroicons_outline:megaphone',
                                    execute: (componentInstance) => {
                                        componentInstance.sendMessageToAllUsers();
                                    },
                                },
                            ],
                            rowDependentActions: [
                                {
                                    label: 'players.messages-list.composeMsg',
                                    icon: 'heroicons_outline:paper-airplane',
                                    execute: (componentInstance) => {
                                        componentInstance.sendMessageToSelectedRows();
                                    },
                                },
                            ],
                        },
                        loadComponent: () =>
                            import('./pages/new-msg/new-msg.component').then(
                                (c) => c.NewMessageComponent
                            ),
                    },
                ],
            },
            // ---------------------------------------------------------------------------------
            // Player Register Sessions
            // ---------------------------------------------------------------------------------
            {
                path: 'player-register-sessions',
                canActivate: [PermissionGuard],
                data: {
                    breadcrumbs: [{ label: 'players.register-sessions.title' }],
                    actions: [
                        {
                            label: '',
                            icon: 'heroicons_outline:cog-6-tooth',
                            execute: (componentInstance) => {
                                componentInstance.openTableColumnSettings();
                            },
                        },
                    ],
                },
                component: TabbedPageLayoutComponent,
                children: [
                    {
                        path: '',
                        loadComponent: () =>
                            import('./pages/register-sessions/player-register-sessions.component').then(
                                (c) => c.PlayerRegisterSessionsComponent
                            ),
                    },
                ],
            },
            // ---------------------------------------------------------------------------------
            // Single Player Profile
            // ---------------------------------------------------------------------------------
            {
                path: ':id',
                canActivate: [PermissionGuard],
                data: {
                    tabs: [
                        // { label: 'players.inner-tabs.summary', route: 'general' },
                        { label: 'players.inner-tabs.social-preferences', route: 'settings' },
                        { label: 'financial.transactions.title', route: 'transactions' },
                        { label: 'players.inner-tabs.verification', route: 'verification' },
                        { label: 'players.inner-tabs.messages', route: 'messages' },
                        { label: 'players.inner-tabs.changesHistory', route: 'changes-history' },
                        { label: 'players.inner-tabs.gamesHistory', route: 'games-history' },
                        { label: 'players.inner-tabs.notes', route: 'notes' },
                        { label: 'navigation.promotions', route: 'promotions' },
                        { label: 'navigation.reports.title', route: 'reports' },
                        { label: 'players.tabs.financial', route: 'financial' },
                    ],
                },
                resolve: {
                    player: playerResolver,
                },
                component: TabbedPageLayoutComponent,
                children: [
                    // -------------------------------------------------------------------------
                    // Preferences -> Single social component (no inner tabs)
                    // -------------------------------------------------------------------------
                    {
                        path: 'settings',
                        loadComponent: () =>
                            import(
                                './pages/settings/social-preferences/social-preferences.component'
                            ).then((c) => c.PlayerSocialPreferencesComponent),
                    },
                    // -------------------------------------------------------------------------
                    // Settings -> (General) Just display player info (no inner tabs)
                    // -------------------------------------------------------------------------
                    // {
                    //     path: 'general',
                    //     loadComponent: () =>
                    //         import(
                    //             './components/player-form/player-form.component'
                    //         ).then((c) => c.PlayerFormComponent),
                    // },
                    // -------------------------------------------------------------------------
                    // Transactions
                    // (id & resolved player still accessible)
                    // -------------------------------------------------------------------------
                    {
                        path: 'transactions',
                        loadComponent: () =>
                            import(
                                './pages/transactions/player-transactions.component'
                            ).then((c) => c.PlayerTransactionsComponent),
                    },
                    // -------------------------------------------------------------------------
                    // Verification -> Documents
                    // (Unchanged; direct component)
                    // -------------------------------------------------------------------------
                    {
                        path: 'verification',
                        data: {
                            innerTabs: [
                                { label: 'players.tabs.documents', route: 'documents' },
                                { label: 'KYC', route: 'kyc' },
                            ]
                        },
                        component: InnerTabbedPageLayoutComponent,
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                redirectTo: 'documents',
                            },
                            {
                                path: 'documents',
                                loadComponent: () =>
                                    import(
                                        './pages/documents/player-documents.component'
                                    ).then((c) => c.PlayerDocumentsComponent),
                            },
                            {
                                path: 'kyc',
                                loadComponent: () =>
                                    import(
                                        './pages/kyc/kyc.component'
                                    ).then((c) => c.PlayerKYCComponent),
                            },
                        ]
                    },
                    // -------------------------------------------------------------------------
                    // Messages -> Single messages component
                    // -------------------------------------------------------------------------
                    {
                        path: 'messages',
                        data: {
                            innerTabs: [
                                { label: 'Messages List', route: 'messages-list' },
                                { label: 'User Chat', route: 'user-chat' },
                                { label: 'players.inner-tabs.notifications', route: 'notifications' },
                            ],
                        },
                        component: InnerTabbedPageLayoutComponent,
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                redirectTo: 'messages-list',
                            },
                            {
                                path: 'messages-list',
                                // Replace with the component that displays your table:
                                loadComponent: () =>
                                    import(
                                        './pages/messages/messages-list.component'
                                    ).then(
                                        (c) => c.PlayersMessagesList
                                    ),
                            },
                            {
                                path: 'user-chat',
                                loadComponent: () =>
                                    import(
                                        './pages/messages/player-messages.component'
                                    ).then((c) => c.UserMessagesComponent),
                            },
                            {
                                path: 'notifications',
                                loadComponent: () =>
                                    import(
                                        './pages/notifications/player-notifications.component'
                                    ).then((c) => c.PlayerNotificationsComponent),
                            },
                        ],
                    },
                    // -------------------------------------------------------------------------
                    // Changes History -> Temporarily load PlayerFormComponent
                    // (id & resolved player still accessible)
                    // -------------------------------------------------------------------------
                    // {
                    //     path: 'changes-history',
                    //     loadComponent: () =>
                    //         import(
                    //             './components/player-form/player-form.component'
                    //         ).then((c) => c.PlayerFormComponent),
                    // },
                    // -------------------------------------------------------------------------
                    // Notes -> Single notes component
                    // -------------------------------------------------------------------------
                    {
                        path: 'notes',
                        loadComponent: () =>
                            import('./pages/notes/player-notes.component').then(
                                (c) => c.PlayerNotesComponent
                            ),
                    },
                    // -------------------------------------------------------------------------
                    // Financial -> Show KPIs only (no inner tabs)
                    // -------------------------------------------------------------------------
                    {
                        path: 'financial',
                        loadComponent: () =>
                            import(
                                './pages/financial/kpis/player-financial-kpis.component'
                            ).then((c) => c.PlayerFinancialKpisComponent),
                    },
                    // -------------------------------------------------------------------------
                    // Promotions
                    // -------------------------------------------------------------------------
                    {
                        path: 'promotions',
                        loadComponent: () =>
                            import(
                                './pages/promotions/player-promotions.component'
                            ).then((c) => c.PlayerPromotionsComponent),
                    },
                    // -------------------------------------------------------------------------
                    // Reports -> KEEP inner tabs
                    // -------------------------------------------------------------------------
                    {
                        path: 'reports',
                        data: {
                            innerTabs: [
                                {
                                    label: 'players.inner-tabs.sportsbook-history',
                                    route: 'sportsbook-history',
                                },
                                {
                                    label: 'players.inner-tabs.sports-bets',
                                    route: 'sports-bets',
                                },
                            ],
                        },
                        component: InnerTabbedPageLayoutComponent,
                        children: [
                            {
                                path: '',
                                redirectTo: 'sportsbook-history',
                                pathMatch: 'full',
                            },
                            {
                                path: 'sportsbook-history',
                                loadComponent: () =>
                                    import(
                                        './pages/reports/sportsbook/sportsbook.component'
                                    ).then(
                                        (c) =>
                                            c.PlayerReportsSportsBookComponent
                                    ),
                            },
                            {
                                path: 'sports-bets',
                                loadComponent: () =>
                                    import(
                                        './pages/reports/sportsbets/sportsbets.component'
                                    ).then(
                                        (c) =>
                                            c.PlayerReportsSportsBetsComponent
                                    ),
                            },
                        ],
                    },
                    // -------------------------------------------------------------------------
                    // Games History -> Inner tabs for Sport and Casino
                    // -------------------------------------------------------------------------
                    {
                        path: 'games-history',
                        data: {
                            innerTabs: [
                                { label: 'players.inner-tabs.casino', route: 'casino' },
                                { label: 'players.inner-tabs.sport', route: 'sport' }
                            ],
                        },
                        component: InnerTabbedPageLayoutComponent,
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                redirectTo: 'casino',
                            },
                            {
                                path: 'casino',
                                loadComponent: () =>
                                    import(
                                        './pages/game-history/casino/casino-game-history.component'
                                    ).then((c) => c.CasinoGameHistoryComponent),
                            },
                            {
                                path: 'sport',
                                loadComponent: () =>
                                    import(
                                        './pages/game-history/sport/sport-game-history.component'
                                    ).then((c) => c.SportGameHistoryComponent),
                            },
                        ],
                    },
                ],
            },
        ],
    },
];
